import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import has from 'just-has';
import get from 'just-safe-get';
import classNames from 'classnames';
import { Button } from '../../../visual-components/button/button';
import { Checkbox } from '../../../visual-components/checkbox/checkbox';
import { FullPageLoader } from '../../../visual-components/full-page-loader/full-page-loader';
import { GridContainer, GridItem } from '../../../visual-components/grid/grid';
import { Link } from '../../../visual-components/link/link';
import { Spacer } from '../../../visual-components/spacer/spacer';
import { Typography } from '../../../visual-components/typography/typography';
import { PageContainer } from '../../page-container/page-container';
import { FormFieldResponseDtoUnion, FormRequestBody, FormType } from '../../backend/openapi/api-types';
import { FormInput } from '../types';
import { NormalizedError } from '../../backend/errors';
import isNil, { notNil } from '../../../core/helpers/is-nil';
import { useTranslatedNormalizedErrors } from '../../backend/use-translated-errors';
import { getLoggerInstance } from '../../../core/monitoring/logger';
import { InlineNotification } from '../../../visual-components/inline-notification/inline-notification';
import { DefinitionList } from '../../../visual-components/definition-list/definition-list';
import { DefinitionBasic } from '../../../visual-components/definition-list/definition-renderers/definition-basic/definition-basic';
import GdprConsent from './gdpr-consent';
import { FormFieldInput } from './form-field-input';
import styles from './form-layout.module.scss';

const logger = getLoggerInstance('form-layout');

const GENERIC_ERROR_FIELD = 'root.serverError';

type Props = {
  documentTitle: string;
  fields: FormFieldResponseDtoUnion[];
  formType: FormType;
  hintText?: string;
  isLoadingFields: boolean;
  isPendingFormRequest: boolean;
  linkBackTo: { to: string; label: string };
  permissionToContact?: boolean;
  sendFormRequest: (body: FormRequestBody) => Promise<void>;
};

const FIELDS_VALIDATION_PREFIX = 'fields';

export const FormLayout: React.FC<Props> = ({
  documentTitle,
  fields,
  formType,
  hintText,
  isLoadingFields,
  isPendingFormRequest,
  linkBackTo,
  permissionToContact = false,
  sendFormRequest,
}) => {
  const { getSafeTranslatedError } = useTranslatedNormalizedErrors();
  const { t } = useTranslation();

  const methods = useForm<FormInput>({
    resetOptions: {
      keepDirtyValues: true,
    },
  });
  const {
    register,
    formState: { errors },
  } = methods;

  const [isChecked, setIsChecked] = useState(false);
  const [isPermissionToContact, setIsPermissionToContact] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (formInput: FormInput) => {
    const body: FormRequestBody = {
      formType: formType,
      fields: formInput,
    };

    try {
      await sendFormRequest(body);
      setIsSubmitted(true);
    } catch (e) {
      if (e instanceof NormalizedError) {
        const { messages } = e;

        for (const item of messages) {
          const { normalizedMessage, path: prefixedPath } = item;
          const translatedMessage = getSafeTranslatedError(normalizedMessage);

          let path = prefixedPath;
          if (prefixedPath?.[0] === FIELDS_VALIDATION_PREFIX) {
            path = prefixedPath.slice(1);
          }
          const joinedPath = path?.join('.');

          if (isNil(joinedPath) || !has(formInput, joinedPath)) {
            logger.warn(`The path ${joinedPath} does not exist on the form`);
            methods.setError(GENERIC_ERROR_FIELD, {
              type: 'custom',
              message: translatedMessage,
            });
            return;
          }

          methods.setError(joinedPath, {
            type: 'custom',
            message: translatedMessage,
          });
        }
      } else {
        logger.warn(`An unexpected error occurred while submitting the form`, { exception: e });
        methods.setError(GENERIC_ERROR_FIELD, {
          type: 'custom',
          message: getSafeTranslatedError('generic_server_error'),
        });
      }
    }
  };

  const genericErrorMessage = get(errors, GENERIC_ERROR_FIELD)?.message;

  useEffect(() => {
    if (isSubmitted) {
      window.scroll(0, 0);
    }
  }, [isSubmitted]);

  if (isLoadingFields) {
    return <FullPageLoader />;
  }

  const hiddenFields = fields.filter((field) => field.isDisabled);
  const hasAnyHiddenFields = hiddenFields.length > 0;

  return (
    <PageContainer documentTitle={documentTitle}>
      {isSubmitted ? (
        <>
          <GridContainer>
            <GridItem span="8">
              <Typography variant="headline-1">{t('form.success.title')}</Typography>
            </GridItem>
          </GridContainer>
          <Spacer size="06" />
          <GridContainer>
            <GridItem span="8">
              <Typography variant="leadtext">{t('form.success.leadtext')}</Typography>
            </GridItem>
          </GridContainer>
          <Spacer size="06" />
          <GridContainer>
            <GridItem span="8">
              <Link icon="arrow-left" to={linkBackTo.to} variant="regular">
                {linkBackTo.label}
              </Link>
            </GridItem>
          </GridContainer>
        </>
      ) : (
        <>
          <GridContainer>
            <GridItem span="8">
              <Typography variant="headline-1">{documentTitle}</Typography>
            </GridItem>
          </GridContainer>
          <Spacer size="06" />
          <GridContainer>
            <GridItem span="8">
              <Typography variant="leadtext">{t('form.generalLeadtext')}</Typography>
            </GridItem>
          </GridContainer>
          <Spacer size="06" />
          {hasAnyHiddenFields ? (
            <GridContainer>
              <GridItem span="8">
                <DefinitionList wide title={t('form.providedInformation.title')}>
                  {hiddenFields.map((field) => {
                    const options = 'options' in field ? field.options : undefined;
                    if (options && 'value' in field) {
                      const option = options.find((option) => option.value === field.value);
                      if (option) {
                        return (
                          <DefinitionBasic key={field.id} term={field.label}>
                            {option.label}
                          </DefinitionBasic>
                        );
                      }
                    }

                    const value = 'value' in field ? field.value : null;
                    const formattedValue = isNil(value) ? '–' : value;
                    return (
                      <DefinitionBasic key={field.id} term={field.label}>
                        {formattedValue}
                      </DefinitionBasic>
                    );
                  })}
                </DefinitionList>
                <Spacer size="04" />
              </GridItem>
            </GridContainer>
          ) : null}

          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              {fields.map((field) => (
                <GridContainer key={field.id} className={classNames({ [styles.hidden]: field.isDisabled })}>
                  <GridItem span="8">
                    <FormFieldInput field={field} register={register} />
                  </GridItem>
                </GridContainer>
              ))}
              <GridContainer>
                <GridItem span="8">
                  {notNil(hintText) ? (
                    <>
                      <Typography variant="maintext">{hintText}</Typography>
                      <Spacer size="06" />
                    </>
                  ) : null}
                  <Checkbox checked={isChecked} onChange={(checked) => setIsChecked(checked)}>
                    <GdprConsent />
                  </Checkbox>
                  {permissionToContact ? (
                    <>
                      <Spacer size="04" />
                      <Checkbox
                        checked={isPermissionToContact}
                        onChange={(checked) => setIsPermissionToContact(checked)}
                      >
                        <Typography variant="maintext">{t('form.permissionToContact')}</Typography>
                      </Checkbox>
                    </>
                  ) : undefined}
                </GridItem>
              </GridContainer>
              <Spacer size="07" />
              {notNil(genericErrorMessage) ? (
                <GridContainer>
                  <GridItem span="8">
                    <InlineNotification title={genericErrorMessage} type="error" />
                    <Spacer size="07" />
                  </GridItem>
                </GridContainer>
              ) : null}
              <GridContainer>
                <GridItem span="8">
                  <Button
                    className={styles.formSubmitButton}
                    disabled={!isChecked || (permissionToContact && !isPermissionToContact) || isPendingFormRequest}
                    icon="mail"
                    type="submit"
                    variant="primary"
                  >
                    {t('form.send')}
                  </Button>
                </GridItem>
              </GridContainer>
            </form>
          </FormProvider>
        </>
      )}
    </PageContainer>
  );
};
